import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useClientMembers,
  useCurrentClient,
} from "@/features/clients/hooks/use-clients";
import type {
  CalendarEvent,
  Schedule,
  TimeOff,
} from "@/features/calendar/types";
import type { Reminder } from "@/features/reminders/types";
import { useDateFnsLocale } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import { useMemo } from "react";
import { DayColumn } from "./column-day";
import { TimeColumn } from "./column-time";
import { useCalendarView } from "../hooks/use-calendar-view";
import { useDragDrop } from "../hooks/use-drag-drop";
import { useWorkHoursScroll } from "../hooks/use-work-hours-scroll";

interface CalendarWeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  reminders?: Reminder[];
  schedules?: Schedule[];
  timeOffs?: TimeOff[];
  onSlotClick: (startTime: number, endTime: number) => void;
  onEventClick: (event: CalendarEvent) => void;
  selectedMembers: string[];
  selectedServices: string[];
}

export function CalendarWeekView({
  currentDate,
  events,
  reminders = [],
  schedules = [],
  timeOffs = [],
  onSlotClick,
  onEventClick,
  selectedMembers,
  selectedServices,
}: CalendarWeekViewProps) {
  const locale = useDateFnsLocale();
  const { timeSlots, filterEvents } = useCalendarView();
  const { scrollAreaRef } = useWorkHoursScroll(schedules);
  const clientId = useCurrentClient()?.client?.id || "";
  const { data: members = [] } = useClientMembers(clientId);
  const {
    draggedEvent,
    dragOverSlot,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragDrop();

  // Generate week days (Monday to Sunday)
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  // Filter events
  const filteredEvents = useMemo(
    () => filterEvents(events, selectedMembers, selectedServices, members),
    [events, selectedMembers, [], selectedServices, members, filterEvents],
  );

  return (
    <div className="flex h-full flex-col">
      {/* The days, on one line each: "lun. 28". The header was two lines
          of large type over mostly white, taller than an hour of the grid. */}
      <div className="grid grid-cols-[70px_repeat(7,1fr)] border-b">
        <div className="border-r" />
        {weekDays.map((day, i) => {
          const today = isSameDay(day, new Date());
          return (
            <div
              key={day.toISOString()}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2",
                i > 0 && "border-l",
              )}
            >
              <span
                className={cn(
                  "text-xs font-medium uppercase text-muted-foreground",
                  today && "text-primary-strong",
                )}
              >
                {format(day, "EEE", { locale })}
              </span>
              <span
                className={cn(
                  "flex size-7 items-center justify-center rounded-full text-sm font-semibold tabular-nums",
                  today && "bg-primary text-primary-foreground",
                )}
              >
                {format(day, "d", { locale })}
              </span>
            </div>
          );
        })}
      </div>

      {/* Calendar grid */}
      <ScrollArea className="flex-1" ref={scrollAreaRef}>
        <div className="relative grid grid-cols-[70px_repeat(7,1fr)]">
          {/* Time labels */}
          <TimeColumn timeSlots={timeSlots} />

          {/* Day columns */}
          {weekDays.map((day, i) => (
            <DayColumn
              key={day.toISOString()}
              className={cn(i > 0 && "border-l")}
              day={day}
              dayIndex={i}
              events={filteredEvents.filter((event) => {
                const eventDate = new Date(event.start_time);
                return isSameDay(eventDate, day);
              })}
              reminders={reminders.filter((reminder) =>
                isSameDay(new Date(reminder.scheduled_at), day),
              )}
              schedules={schedules}
              timeOffs={timeOffs}
              selectedMembers={selectedMembers}
              onEventClick={onEventClick}
              onSlotClick={onSlotClick}
              draggedEvent={draggedEvent}
              dragOverSlot={dragOverSlot}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
